import { parseAll } from '../../../../common/src/ts/parser';
import * as CFI from '../../../../common/src/ts/cfi';
import { FitStyle, ScrollBase } from '../utils/view';
import { ScaleBehavior } from '../components/app/SheetScaler';
import { Stamp } from '../drawing/types';

export enum PageSpread {
  Left,
  Right,
  Center,
}

export enum GlobalDirection {
  LTR,
  RTL,
}

export enum ExtraToolAction {
  GoToPage,
  OpenURL,
  OpenTab,
  OpenPopup,
  SendMessage,
}

export interface ContentFeatureDefinition {
  readonly id: string;
  readonly name: string;
  readonly defaultValue: boolean;
}

interface ExtraToolDefinitionBase {
  readonly id: string;
  readonly name: string;
  readonly icon: string;
}

interface ExtraToolDefinitionGoToPage extends ExtraToolDefinitionBase {
  readonly action: ExtraToolAction.GoToPage;
  readonly pageIndex: number;
}

interface ExtraToolDefinitionOpenURL extends ExtraToolDefinitionBase {
  readonly action: ExtraToolAction.OpenURL;
  readonly url: string;
}

interface ExtraToolDefinitionOpenTab extends ExtraToolDefinitionBase {
  readonly action: ExtraToolAction.OpenTab;
  readonly path: string;
}

interface ExtraToolDefinitionOpenPopup extends ExtraToolDefinitionBase {
  readonly action: ExtraToolAction.OpenPopup;
  readonly path: string;
  readonly data: any;
}

interface ExtraToolDefinitionSendMessage extends ExtraToolDefinitionBase {
  readonly action: ExtraToolAction.SendMessage;
  readonly data: any;
}

export type ExtraToolDefinition = (
  ExtraToolDefinitionGoToPage | ExtraToolDefinitionOpenURL | ExtraToolDefinitionOpenTab
  | ExtraToolDefinitionOpenPopup | ExtraToolDefinitionSendMessage
);

export interface Thumbnail {
  readonly path: string;
}

interface SpineItemBaseCommon {
  readonly epubcfi: string;
  readonly path: string;
  readonly providers: { readonly [component: string]: true };
  readonly features: ReadonlyArray<string>;
  readonly extraTools: ReadonlyArray<string>;
  readonly title: string;
  readonly thumbnail?: Thumbnail;
  readonly minWidth?: number;
  readonly minHeight?: number;
  readonly maxWidth?: number;
  readonly maxHeight?: number;
  readonly favicon?: string;
}

interface SpineItemBaseFXL extends SpineItemBaseCommon {
  readonly fxl: true;

  readonly width: number;
  readonly height: number;
  readonly fitStyle: FitStyle;
  readonly scrollBase: ScrollBase;
  readonly scaleBehavior: ScaleBehavior;
  readonly supportsReflowableContentGeneration: boolean;
}

interface SpineItemBaseReflowable extends SpineItemBaseCommon {
  readonly fxl: false;

  // ReflowableではFXLと少し違う意味合いで使う
  // FitScreen: ビューア側で拡大等を一切せず、単にcontainer sizeに合わせるモード
  // FitWidth: 横書き(縦方向は自動リサイズ), transform形式での拡大をサポート
  // FitHeight: 縦書き(横方向は自動リサイズ), transform形式での拡大をサポート
  readonly fitStyle: FitStyle;
}

interface SpineItemBaseLinear {
  readonly linear: true;
  readonly linearIndex: number;
  readonly pageSpread: PageSpread;
}

interface SpineItemBaseNonLinear {
  readonly linear: false;
}

type SpineItemBaseAnyStyle = SpineItemBaseFXL | SpineItemBaseReflowable;
export type SpineItem = SpineItemBaseAnyStyle & (SpineItemBaseLinear | SpineItemBaseNonLinear);
export type LinearSpineItem = SpineItemBaseAnyStyle & SpineItemBaseLinear;

export interface SpreadBlock {
  readonly left?: LinearSpineItem;
  readonly right?: LinearSpineItem;
  readonly center?: LinearSpineItem;
}

export interface IndexedSpreadBlock extends SpreadBlock {
  readonly index: number;
}

export interface NavigationItem {
  readonly title: string;
  readonly href: string;
}

type ItemReverseMap = { [fullPath: string]: SpineItem };

export interface ResourceDivision {
  readonly firstLinearItem: number;
}

export interface Rendition {
  readonly title: string;
  readonly direction: GlobalDirection;
  readonly allItems: ReadonlyArray<SpineItem>;
  readonly linearItems: ReadonlyArray<LinearSpineItem>;
  readonly toc: ReadonlyArray<NavigationItem>;
  readonly itemIndices: ItemReverseMap;
  readonly opf: string;
  readonly extraNav: string | undefined;
  readonly divisions: { readonly [componentID: string]: ResourceDivision };
}

export interface Component {
  readonly id: string;
  readonly title: string;
}

export type Junction = { readonly [opf: string]: CFI.Path };

export interface Publication {
  readonly id: string;
  readonly resourceDir: string;
  readonly renditions: ReadonlyArray<Rendition>;
  readonly components: ReadonlyArray<Component>;
  readonly mapping: ReadonlyArray<Junction>;
  readonly featureDefinitions: ReadonlyArray<ContentFeatureDefinition>;
  readonly extraToolDefinitions: ReadonlyArray<ExtraToolDefinition>;
  readonly extraStamps: ReadonlyArray<Stamp>;
}

// Parsing and utility functions

export namespace JSONParsing {
  interface ThumbnailData {
    path: string;
  }

  export interface SpineItemData {
    epubcfi: string;
    path: string;
    linear: boolean;
    pageSpread?: 'left' | 'right' | 'center';
    width?: number;
    height?: number;
    minWidth?: number;
    minHeight?: number;
    maxWidth?: number;
    maxHeight?: number;
    fitStyle?: 'screen' | 'width' | 'height';
    scrollBase?: 'center' | 'left' | 'right';
    scaling?: 'transform' | 'resize';
    supportsReflowableContentGeneration?: boolean;
    features?: string[];
    extraTools?: string[];
    title?: string;
    favicon?: string;
    thumbnail?: ThumbnailData;
  }

  export interface NavigationItemData {
    title: string;
    href: string;
  }

  export interface ResourceDivisionData {
    spineItems: string[];
  }

  export interface RenditionData {
    title: string;
    spine: SpineItemData[];
    toc?: NavigationItemData[];
    path: string;
    extraNav?: string;
    resourceDivisions?: { [id: string]: ResourceDivisionData };
  }

  export interface ResourceDivisionReferenceData {
    rendition: string;
    idref: string;
  }

  export interface ComponentData {
    id: string;
    title: string;
    resourceDivisions: ResourceDivisionReferenceData[];
  }

  export interface JointData {
    rendition: string;
    epubcfi: string;
  }

  export interface JunctionData {
    joints: JointData[];
  }

  interface ContentFeatureDefinitionData {
    id: string;
    name: string;
    defaultValue: boolean;
  }

  interface ExtraToolDefinitionData {
    id: string;
    icon: string;
    name: string;
    action: 'go-to-page' | 'open-url' | 'open-tab' | 'open-popup' | 'send-message';
    pageIndex?: number;
    url?: string;
    path?: string;
    data?: any;
  }

  interface ExtraStampDefinitionData {
    id: string;
    name: string;
    width: number;
    height: number;
    path: string;
  }

  export interface PublicationData {
    renditions: RenditionData[];
    components?: ComponentData[];
    mapping?: JunctionData[];
    featureDefinitions?: ContentFeatureDefinitionData[];
    extraToolDefinitions?: ExtraToolDefinitionData[];
    extraStampDefinitions?: ExtraStampDefinitionData[];
  }

  export const parsePageSpread = (value: string | undefined): PageSpread | undefined => {
    if (value === undefined) {
      return undefined;
    }

    switch (value) {
      case 'left': return PageSpread.Left;
      case 'right': return PageSpread.Right;
      case 'center': return PageSpread.Center;
      default: throw new Error(`Unexpected page spread value ${value}`);
    }
  };

  export const parseFitStyle = (value: string | undefined, defVal: FitStyle): FitStyle => {
    switch (value) {
      case 'width':
        return FitStyle.FitWidth;
      case 'height':
        return FitStyle.FitHeight;
      case 'screen':
        return FitStyle.FitScreen;
      case undefined:
        return defVal;
      default:
        throw new Error(`Unexpected fitStyle value ${value}`);
    }
  };

  export const parseScaleBehavior = (value: string | undefined): ScaleBehavior => {
    switch (value) {
      case 'resize':
        return ScaleBehavior.Resize;
      case 'transform':
      case undefined:
        return ScaleBehavior.Transform;
      default:
        throw new Error(`Unexpected scaleBehavior value ${value}`);
    }
  };

  export const parseScrollBase = (value: string | undefined): ScrollBase => {
    switch (value) {
      case 'center':
        return ScrollBase.Center;
      case 'left':
        return ScrollBase.LeftIfLargerThanScreen;
      case 'right':
        return ScrollBase.RightIfLargerThanScreen;
      case undefined:
        return ScrollBase.Center;
      default:
        throw new Error(`Unexpected scrollBase value ${value}`);
    }
  };

  export const parseExtraToolDefinitions = (
    value: ExtraToolDefinitionData,
  ): ExtraToolDefinition => {
    const common = {
      id: value.id,
      name: value.name,
      icon: value.icon,
    };

    switch (value.action) {
      case 'go-to-page': {
        const pageIndex = value.pageIndex;
        if (pageIndex == null) {
          throw new Error('Attribute "pageIndex" is required for "go-to-page" action.');
        }
        return {
          ...common,
          pageIndex,
          action: ExtraToolAction.GoToPage,
        };
      }
      case 'open-url': {
        const url = value.url;
        if (url == null) {
          throw new Error('Attribute "url" is required for "open-url" action.');
        }
        return {
          ...common,
          url,
          action: ExtraToolAction.OpenURL,
        };
      }
      case 'open-tab': {
        const path = value.path;
        if (path == null) {
          throw new Error('Attribute "path" is required for "open-tab" action.');
        }
        return {
          ...common,
          path,
          action: ExtraToolAction.OpenTab,
        };
      }
      case 'open-popup': {
        const path = value.path;
        if (path == null) {
          throw new Error('Attribute "path" is required for "open-popup" action.');
        }

        const data = value.data;
        return {
          ...common,
          path,
          data,
          action: ExtraToolAction.OpenPopup,
        };
      }
      case 'send-message': {
        const data = value.data;
        return {
          ...common,
          data,
          action: ExtraToolAction.SendMessage,
        };
      }
      default:
        throw new Error(`Unexpected extraToolDefinition.action value ${value.action}`);
    }
  };

  export function parseThumbnail(thumb: ThumbnailData): Thumbnail {
    return { path: thumb.path };
  }
}

const nextImplicitSpread = (direction: GlobalDirection, prevSpread: PageSpread): PageSpread => {
  const rtl = direction === GlobalDirection.RTL;
  switch (prevSpread) {
    case PageSpread.Center:
      return rtl ? PageSpread.Right : PageSpread.Left;
    case PageSpread.Left:
      return PageSpread.Right;
    case PageSpread.Right:
      return PageSpread.Left;
  }
};

const toBaseSpineItem = (
  data: JSONParsing.SpineItemData,
  providers: { readonly [id: string]: true } = {},
): SpineItemBaseAnyStyle => {
  const common = {
    epubcfi: data.epubcfi,
    path: data.path,
    providers: { ...providers, '*': true as true },
    features: data.features ? data.features : [],
    extraTools: data.extraTools ? data.extraTools : [],
    title: data.title || data.path,
    thumbnail: data.thumbnail && JSONParsing.parseThumbnail(data.thumbnail),
    minWidth: data.minWidth,
    minHeight: data.minHeight,
    maxWidth: data.maxWidth,
    maxHeight: data.maxHeight,
    favicon: data.favicon,
  };

  if (data.width === undefined || data.height === undefined) {
    // reflowable
    return {
      ...common,
      fxl: false,
      fitStyle: JSONParsing.parseFitStyle(data.fitStyle, FitStyle.FitWidth),
    };
  }

  return {
    ...common,
    fxl: true,
    width: data.width,
    height: data.height,
    fitStyle: JSONParsing.parseFitStyle(data.fitStyle, FitStyle.FitScreen),
    scrollBase: JSONParsing.parseScrollBase(data.scrollBase),
    scaleBehavior: data.scaling === 'resize' ? ScaleBehavior.Resize : ScaleBehavior.Transform,
    supportsReflowableContentGeneration: data.supportsReflowableContentGeneration === true,
  };
};

const parseRendition = (
  data: Readonly<JSONParsing.RenditionData>,
  providerMap: { readonly [epubcfi: string]: { readonly [component: string]: true } } = {},
): Rendition => {
  const { title, extraNav, path: opf } = data;
  const direction: GlobalDirection = GlobalDirection.LTR; // TODO : Support RTL.
  const toc: ReadonlyArray<NavigationItem> = data.toc !== undefined ? data.toc : [];

  const itemIndices: ItemReverseMap = {};
  const allItems: SpineItem[] = [];
  const linearItems: LinearSpineItem[] = [];
  let lastSpread: PageSpread = PageSpread.Center;

  const divisions: { [componentID: string]: ResourceDivision } = {};

  for (const itemData of data.spine) {
    let item: SpineItem;

    // TODO : Remove assertions from epubcfi.
    const providers = providerMap[itemData.epubcfi];

    if (itemData.linear) {
      const index = linearItems.length;
      let nextSpread = JSONParsing.parsePageSpread(itemData.pageSpread);
      if (nextSpread === undefined) {
        nextSpread = nextImplicitSpread(direction, lastSpread);
      }
      item = {
        ...toBaseSpineItem(itemData, providers),
        linear: true,
        linearIndex: index,
        pageSpread: nextSpread,
      };
      linearItems.push(item);
      lastSpread = nextSpread;

      for (const componentID in providers) {
        if (componentID in divisions) {
          continue;
        }

        divisions[componentID] = { firstLinearItem: index };
      }
    } else {
      item = {
        ...toBaseSpineItem(itemData, providers),
        linear: false,
      };
    }

    itemIndices[itemData.path] = item;
    allItems.push(item);
  }

  return {
    title,
    direction,
    allItems,
    linearItems,
    toc,
    extraNav,
    itemIndices,
    opf,
    divisions,
  };
};

function parseMultipleRenditionMapping(
  publicationData: Readonly<JSONParsing.PublicationData>,
): Junction[] {
  const parsedMapping: { [opf: string]: CFI.Path }[] = [];

  const { mapping } = publicationData;
  if (mapping !== undefined) {
    if (!(mapping instanceof Array)) {
      throw new Error('mapping property must be array.');
    }

    for (const junction of mapping) {
      if (!(junction.joints instanceof Array)) {
        throw new Error('joints property must be array.');
      }

      const parsedJunction: { [opf: string]: CFI.Path } = {};
      for (const joint of junction.joints) {
        if (
          !(joint instanceof Object)
          || joint.rendition === undefined
          || joint.epubcfi === undefined
        ) {
          throw new Error('Element of joints property must have rendition and epubcfi.');
        }

        const parsedCFI = parseAll(joint.epubcfi, CFI.path);
        if (parsedCFI === undefined) {
          throw new Error('epubcfi property must be valid EPUBCFI path.');
        }

        parsedJunction[joint.rendition] = parsedCFI;
      }

      parsedMapping.push(parsedJunction);
    }
  }

  return parsedMapping;
}

export const parsePublicationData = (
  id: Readonly<string>,
  resourceDir: Readonly<string>,
  publicationData: Readonly<JSONParsing.PublicationData>,
): Publication => {
  if (publicationData.renditions.length === 0) {
    throw new Error('Renditions required.');
  }

  const renditionMap: { [path: string]: JSONParsing.RenditionData } = {};
  for (const rendition of publicationData.renditions) {
    renditionMap[rendition.path] = rendition;
  }

  const { components } = publicationData;
  const providerMap: { [opf: string]: { [epubcfi: string]: { [component: string]: true } } } = {};
  if (components !== undefined) {
    for (const component of components) {
      if (
        component.id === undefined
        || component.title === undefined
        || component.resourceDivisions === undefined
      ) {
        // TODO : Produce an error.
        continue;
      }

      for (const member of component.resourceDivisions) {
        const { rendition: path, idref } = member;
        if (!(path in renditionMap)) {
          // TODO : Produce an error.
          continue;
        }

        const rendition = renditionMap[path];
        const { resourceDivisions } = rendition;
        if (resourceDivisions === undefined || !(idref in resourceDivisions)) {
          // TODO : Produce an error.
          continue;
        }

        const resourceDivision = resourceDivisions[idref];
        if (!(path in providerMap)) {
          providerMap[path] = {};
        }

        for (const epubcfi of resourceDivision.spineItems) {
          // TODO : Remove assertions from epubcfi.
          if (epubcfi in providerMap[path]) {
            providerMap[path][epubcfi][component.id] = true;
          } else {
            providerMap[path][epubcfi] = { [component.id]: true };
          }
        }
      }
    }
  }

  const featureDefinitions = (
    publicationData.featureDefinitions ? publicationData.featureDefinitions : []
  );
  const extraToolDefinitions = (
    publicationData.extraToolDefinitions ?
      publicationData.extraToolDefinitions.map(d => JSONParsing.parseExtraToolDefinitions(d)) :
      []
  );

  const extraStamps: Stamp[] = (
    publicationData.extraStampDefinitions ? publicationData.extraStampDefinitions : []
  );

  return {
    id,
    resourceDir,
    featureDefinitions,
    extraToolDefinitions,
    extraStamps,
    renditions: publicationData.renditions.map(
      data => parseRendition(data, providerMap[data.path]),
    ),
    components: (
      components === undefined
        ? []
        : components.map(data => ({ id: data.id, title: data.title }))
    ),
    mapping: parseMultipleRenditionMapping(publicationData),
  };
};

export function isSpineItemInstalled(item: SpineItem, installedComponents: ReadonlyArray<string>) {
  for (const component of installedComponents) {
    if (component in item.providers) {
      return true;
    }
  }

  return false;
}
